import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { getUpgradeUrl } from '../../../../helpers/upgradeUrl';
import { showMessage } from '../../../../helpers/showMessage';
import { getCurrentSubscriptionQuery } from './query';
import { ACTIVE_STATUSES, formatDate, intervalLabel, isItemTrialing } from './helpers';

// Backend next_action (resolve_subscription_action, customers/services/manage_subscription/
// dispatch.py) meaning "this user is on the old billing system and their subscription can be
// converted to the new one" — the only state in which the upgrade CTA makes sense.
const NEXT_ACTION_MIGRATE = 'migrate';

// Where the add-ons row's "buy more" CTA points. Each purchasable add-on is a collapse on that
// page, selectable via ?part= (see ADD_ON_TYPES_LIST in customers/defaults.py); no part is
// pre-selected here because the row covers all of them at once.
const ADD_ONS_URL = '/tools/add-ons/';

/**
 * "Current Plan" card driven by GET /api/v2/accounts/subscription/.
 *
 * One card, one labelled line per fact: Plan (name, plus [legacy] / status / billing-interval
 * tags), Products, Features, Add-ons and Renewal Date.
 *
 * Old-system (v1) users are described entirely from `legacy_plan`: its feature booleans are the
 * Products, its domain/seat counts the Features, and the user's Profile add-ons the Add-ons. Their
 * `items` are deliberately ignored — GetCurrentSubscriptionService._build_legacy_items synthesizes
 * those by projecting the very same legacy plan onto new-system products, so rendering them too
 * would state one set of entitlements twice. They also get the upgrade CTA, shown whenever the
 * backend resolved next_action to "migrate".
 *
 * New-system (v2) users get their real subscription items as Products. The payload exposes no
 * entitlement counts or Profile add-ons for them (`legacy_plan` is null), so those two rows are
 * skipped rather than guessed at — the change-plan flow owns those numbers. An item-level trial
 * (ADR 0008) leaves the subscription itself "active", so a trialing item is called out inline in
 * Products with its end date instead of through the status tag.
 *
 * Pricing is deliberately not shown here; the change-plan flow owns that.
 */
class PlanProducts extends Component {
    constructor() {
        super();

        this.is_not_viewer = window.is_not_viewer;

        this.state = {
            loading: true,
            subscription: null,
        };
    }

    async componentDidMount() {
        try {
            const response = await getCurrentSubscriptionQuery();
            const data = await response.json();

            if (response.ok) {
                this.setState({ subscription: data, loading: false });
            } else {
                showMessage(data?.detail || this.props.t('settings.plan_products.error'), 'error');
                this.setState({ loading: false });
            }
        } catch (err) {
            showMessage(err?.message || this.props.t('settings.plan_products.error'), 'error');
            this.setState({ loading: false });
        }
    }

    /** True while the subscription exists and still grants access (active or trialing). */
    hasActiveSubscription() {
        const { subscription } = this.state;
        return Boolean(
            subscription?.subscription_id && ACTIVE_STATUSES.includes(subscription?.status),
        );
    }

    renderHeaderActions() {
        const { t } = this.props;
        const { subscription } = this.state;

        if (!this.is_not_viewer || !subscription) return null;

        if (subscription.is_eligible_for_resubscribe) {
            return (
                <div className="settings-plan-header-btns">
                    <a
                        className="settings-secondary-btn"
                        href={getUpgradeUrl('/customers/change_plan/?reactivation=true')}
                    >
                        {t('settings.plan.reactivate')}
                    </a>
                    {this.props.customer_id !== 'False' && (
                        <a
                            className="settings-secondary-btn"
                            href={`/settings/payment-methods/${this.props.customer_id}`}
                        >
                            {t('settings.plan.payment_methods')}
                        </a>
                    )}
                </div>
            );
        }

        return (
            <div className="settings-plan-header-btns">
                <a
                    className="settings-secondary-btn"
                    href="#"
                    role="button"
                    id="planProductsMenuLink"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                >
                    <button className="btn-icon d-flex">
                        {t('settings.plan_products.billing')}
                    </button>
                </a>
                <ul className="dropdown-menu" aria-labelledby="planProductsMenuLink">
                    <li>
                        <a className="dropdown-item cursor-pointer" href="/customers/licenses/">
                            {t('settings.plan.billing_history')}
                        </a>
                    </li>
                    <li>
                        <a
                            className="dropdown-item cursor-pointer"
                            href={getUpgradeUrl('/customers/change_plan/')}
                        >
                            {t('settings.plan.change_plan')}
                        </a>
                    </li>
                    {!subscription.cancel_at_period_end && (
                        <li>
                            <a
                                className="dropdown-item cursor-pointer"
                                href="/customers/change_plan/?cancellation=true"
                            >
                                {t('settings.plan.deactivate')}
                            </a>
                        </li>
                    )}
                </ul>
            </div>
        );
    }

    /**
     * Names for the Products row: an old-system user's own plan modules, or the real subscription
     * items, each annotated with its trial end date while it is still trialing.
     */
    productNames() {
        const { t } = this.props;
        const { subscription } = this.state;
        const legacyPlan = subscription.legacy_plan;

        if (legacyPlan) {
            const { plan } = legacyPlan;
            return [
                plan.search_analytics && t('settings.plan_products.legacy.search_analytics'),
                plan.discover_analytics && t('settings.plan_products.legacy.discover_analytics'),
                plan.commerce_analytics && t('settings.plan_products.legacy.commerce_analytics'),
                plan.ai_tracking && t('settings.plan_products.legacy.ai_tracking'),
            ].filter(Boolean);
        }

        return (subscription.items || []).map((item) =>
            isItemTrialing(item)
                ? t('settings.plan_products.product_trialing', {
                      product: item.product_name,
                      date: formatDate(item.trial_end),
                  })
                : item.product_name,
        );
    }

    /** "Plan: <name> [legacy] [status] [interval]", with the legacy upgrade CTA on the right. */
    renderPlanRow() {
        const { t } = this.props;
        const { subscription } = this.state;
        const legacyPlan = subscription.legacy_plan;
        const interval = intervalLabel(t, subscription.interval, subscription.interval_count);
        const status = t(`settings.plan_products.statuses.${subscription.status}`, {
            defaultValue: subscription.status,
        });
        // New-system subscriptions are assembled per product and carry no plan name of their own.
        const name = legacyPlan
            ? legacyPlan.plan.name
            : t('settings.plan_products.custom_plan_name');

        return (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="settings-plan-content-left-header">
                    {t('settings.plan_products.plan')} {name}
                    {legacyPlan && (
                        <div className="settings-plan-content-left-tag">
                            {t('settings.plan_products.legacy_tag')}
                        </div>
                    )}
                    <div className="settings-plan-content-left-tag">{status}</div>
                    {interval && <div className="settings-plan-content-left-tag">{interval}</div>}
                </div>
                {this.is_not_viewer && subscription.next_action === NEXT_ACTION_MIGRATE && (
                    <a
                        className="settings-secondary-btn flex-shrink-0"
                        href={getUpgradeUrl('/customers/change_plan/')}
                    >
                        {t('settings.plan_products.upgrade_legacy')}
                    </a>
                )}
            </div>
        );
    }

    renderProductsRow() {
        const { t } = this.props;
        const products = this.productNames();

        if (products.length === 0) return null;

        return (
            <div className="settings-plan-content-left-bottom">
                {t('settings.plan_products.products')} <span>{products.join(', ')}</span>
            </div>
        );
    }

    /** Entitlement counts. Only the legacy payload carries them, so v2 users get no row. */
    renderFeaturesRow() {
        const { t } = this.props;
        const legacyPlan = this.state.subscription.legacy_plan;

        if (!legacyPlan) return null;

        const { plan } = legacyPlan;
        const features = [
            t('settings.plan_products.domains_count', { n: plan.domains }),
            t('settings.plan_products.seats_count', { n: plan.seats }),
        ];

        return (
            <div className="settings-plan-content-left-bottom">
                {t('settings.plan_products.features')} <span>{features.join(', ')}</span>
            </div>
        );
    }

    /** Purchased Profile add-ons, with the "buy more" CTA on the right. Legacy payload only. */
    renderAddOnsRow() {
        const { t } = this.props;
        const legacyPlan = this.state.subscription.legacy_plan;

        if (!legacyPlan) return null;

        const addOns = legacyPlan.add_ons;
        const purchased = [
            t('settings.plan_products.add_on.seats', { n: addOns.number_of_seats }),
            t('settings.plan_products.add_on.content_analysis', {
                n: addOns.number_of_ai_analysis,
            }),
            t('settings.plan_products.add_on.prompt_tracking_credits', {
                n: addOns.number_of_credits,
            }),
            t('settings.plan_products.add_on.prompt_generation_credits', {
                n: addOns.extra_prompt_generation_credits,
            }),
        ];

        return (
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div className="settings-plan-content-left-bottom">
                    {t('settings.plan_products.add_ons')} <span>{purchased.join(', ')}</span>
                </div>
                {this.is_not_viewer && (
                    <a className="settings-secondary-btn flex-shrink-0" href={ADD_ONS_URL}>
                        {t('settings.plan_products.buy_more')}
                    </a>
                )}
            </div>
        );
    }

    /** Renewal date, or the cancellation date once the subscription is set to end. */
    renderRenewalRow() {
        const { t } = this.props;
        const { subscription } = this.state;

        if (!subscription.current_period_end) return null;

        return (
            <div className="settings-plan-content-left-bottom">
                {subscription.cancel_at_period_end
                    ? t('settings.plan_products.cancels_on')
                    : t('settings.plan_products.renewal_date')}{' '}
                <span>{formatDate(subscription.current_period_end)}</span>
            </div>
        );
    }

    renderContent() {
        const { t } = this.props;
        const { loading, subscription } = this.state;

        if (loading) {
            return (
                <div className="settings-plan-content">
                    <div className="settings-plan-content-left-bottom">
                        {t('settings.plan_products.loading')}
                    </div>
                </div>
            );
        }

        if (!this.hasActiveSubscription()) {
            return (
                <div className="settings-plan-content">
                    <div className="settings-plan-content-left-bottom">
                        {t('settings.plan_products.no_subscription')}
                    </div>
                </div>
            );
        }

        return (
            <div className="settings-plan-content">
                <div className="settings-plan-content-left flex-grow-1">
                    {this.renderPlanRow()}
                    {this.renderProductsRow()}
                    {this.renderFeaturesRow()}
                    {this.renderAddOnsRow()}
                    {this.renderRenewalRow()}
                    {/* {subscription.update_warning_message && (
                        <div className="settings-plan-content-left-bottom">
                            <span>{subscription.update_warning_message}</span>
                        </div>
                    )} */}
                </div>
                {subscription.cancel_at_period_end && (
                    <div className="settings-plan-content-right">
                        <a
                            className="button button-filled button-primary"
                            href={getUpgradeUrl('/customers/change_plan/?reactivation=true')}
                        >
                            {t('settings.plan.reactivate_your_account')}
                        </a>
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { t } = this.props;

        return (
            <div className="settings-plan">
                <div className="settings-plan-header">
                    <div className="settings-plan-header-title">
                        {t('settings.plan_products.header')}
                    </div>
                    {this.renderHeaderActions()}
                </div>
                {this.renderContent()}
            </div>
        );
    }
}

export default withTranslation()(PlanProducts);
